import { useColorScheme } from '@mui/material/styles';

/**
 * The categorical palette for charts.
 *
 * These hexes are not a matter of taste and must not be adjusted by eye. They
 * were checked with a palette validator against the surfaces charts actually
 * render on (the card background: #FFFFFF light, #0B2E3D dark) for five things:
 * lightness band, chroma floor, colour-blind separation, normal-vision
 * separation, and contrast against the surface.
 *
 *   light  #0B6E99 ocean · #eb6834 orange · #1baf7a aqua
 *          worst pair ΔE 9.2 (deutan), 21.9 (normal vision) — all pass
 *   dark   #3987e5 blue  · #d95926 orange · #199e70 aqua
 *          worst pair ΔE 9.4 (deutan), 20.9 (normal vision) — all pass
 *
 * Two results worth recording, so nobody "fixes" them back:
 *
 *   - Dark slot 1 is a truer blue than the brand's ocean, not an oversight. The
 *     brand dark tones (#3EA6D0, #0E86B8) both land too close to the aqua on a
 *     navy surface (ΔE 14.5 and 14.9 against a floor of 15) — close enough that
 *     a reader with ordinary colour vision would struggle to tell the two series
 *     apart. Dark steps are chosen for the dark surface, never flipped from
 *     light.
 *   - In light mode the aqua sits at 2.82:1 against white, below the 3:1 bar.
 *     The mitigation is that every chart ships a table view and a legend, so no
 *     value is ever carried by colour alone.
 *
 * The order is fixed and meaningful: slot 1 is always sales, slot 2 always
 * stock, slot 3 always misc. Colour follows the measure, never its size or its
 * position in a filtered list — a reader who learns "orange is stock" must not
 * have that repainted when a filter changes.
 */

const SERIES_COLORS = {
  light: ['#0B6E99', '#eb6834', '#1baf7a'],
  dark: ['#3987e5', '#d95926', '#199e70']
};

/**
 * Outcome colours, for a figure that is genuinely good or bad rather than one
 * category among several.
 *
 * Fixed in both modes, and deliberately distinct from the series slots above so
 * an outcome never impersonates a series. These are the only colours in the
 * application that carry a judgement, and they are never used for a fourth
 * series.
 *
 * Colour is never the sole signal: wherever these appear the figure is also
 * labelled in words ("Profit" / "Loss") and carries an explicit sign, so the
 * meaning survives for a reader who cannot separate the two hues.
 */
export const outcomeColors = {
  good: '#0ca30c',
  bad: '#d03b3b'
};

/**
 * The palette for the scheme currently on screen.
 *
 * `mode` is 'system' until the user picks a theme explicitly, in which case the
 * resolved answer is in `systemMode`. Both are undefined on the very first
 * render, so light is the fallback.
 */
export function useChartColors() {
  const { mode, systemMode } = useColorScheme();
  const resolved = mode === 'system' ? systemMode : mode;

  return SERIES_COLORS[resolved === 'dark' ? 'dark' : 'light'];
}
